import Export16 from '@carbon/icons-react/es/export/16';
import Timer20 from '@carbon/icons-react/es/timer/20';
import { Tooltip } from 'antd';
import { keyBy } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { Category } from 'api/timesheets/categories';
import { Configuration } from 'api/timesheets/configuration/configuration';
import { TimesheetReportsApi } from 'api/timesheets/reports/api';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import { Button } from 'components/atoms/button';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import {
  CustomRangePicker,
  FilterContainer,
} from 'components/organisms/projects-report/common';
import {
  getRangeObject,
  Range,
} from 'components/organisms/projects-report/common/range-picker/types';
import { FilterableField } from 'components/organisms/tasks/fields';
import { ReportEmptyState } from 'components/organisms/timesheets/common/report-empty-state';
import { ReportLoadingState } from 'components/organisms/timesheets/common/report-loading-state';
import { TimesheetOverallReport as TimesheetOverallReportContent } from 'components/organisms/timesheets/reports';
import { useFilterFields } from 'components/organisms/timesheets/reports/filter-fields';
import {
  from_search,
  to_query,
} from 'components/organisms/timesheets/reports/overall-search-param-transformer';
import { useFilterParam } from 'components/organisms/timesheets/reports/use-filter-param';
import { useOverallReportSearchParams } from 'components/organisms/timesheets/reports/use-overall-report-search-param';
import { TimesheetReportViews } from 'components/organisms/timesheets/reports/views';
import { Argon } from 'components/templates';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslation } from 'hooks/use-translation';
import { useProjectsQuery } from 'queries';
import { useNativeActiveOrInvitedUsersQuery } from 'queries/members';
import { RangeOptions } from '../use-date-range-filter';
import { useExport } from '../use-export';
import { useTimesheetReportSummary } from '../use-timesheet-report-summary';

interface Props {
  timesheetCategories: Category[];
  configuration: Configuration;
}
export const TimesheetOverallReport: FC<Props> = ({
  timesheetCategories,
  configuration,
}) => {
  const { fields, isFieldsLoading } = useFilterFields({
    timesheetCategories,
  });
  const [initialized, setInitialized] = useState(false);
  const fields_lookup_by_name = useMemo(
    (): Record<string, FilterableField> =>
      keyBy(isFieldsLoading ? [] : fields, 'fieldName'),
    [fields, isFieldsLoading],
  );
  const defaultRange = useMemo(() => getRangeObject(Range.ThisMonth), []);

  const {
    searchParams,
    replaceSearchParams,
    onFilterChange,
    onViewSelect,
    onRangeChange,
  } = useOverallReportSearchParams(
    fields_lookup_by_name,
    defaultRange,
    isFieldsLoading ? 'loading' : 'loaded',
  );
  const filterParam = useFilterParam(searchParams.filter, initialized);
  const [query, setQuery] = useLocalStorage(
    'timesheets_report.overall_filter',
    '',
  );
  const { search: queryParams } = useLocation();

  useEffect(() => {
    if (!initialized || isFieldsLoading) {
      return;
    }

    setQuery(to_query(searchParams));
  }, [searchParams, initialized, isFieldsLoading, query, setQuery]);

  useEffect(() => {
    if (initialized || isFieldsLoading) {
      return;
    }
    const restore_query = (queryParams ?? '') === '' ? query : queryParams;
    const saved_filter = from_search(
      fields_lookup_by_name,
      getRangeObject(Range.ThisMonth),
    )(restore_query);

    replaceSearchParams(saved_filter);
    setInitialized(true);
  }, [
    initialized,
    setInitialized,
    isFieldsLoading,
    fields_lookup_by_name,
    queryParams,
    replaceSearchParams,
    query,
  ]);

  const summaryApi = useCallback(
    () =>
      filterParam == null
        ? null
        : TimesheetReportsApi.getSummary(
            filterParam.startDate,
            filterParam.endDate,
            filterParam.validFilters,
            filterParam.match,
          ),
    [filterParam],
  );

  const { reportSummary, isSummaryLoading } =
    useTimesheetReportSummary(summaryApi);

  const { exportAsCSV } = useExport({
    range: searchParams.filter.range,
    filters: filterParam?.validFilters,
    match: filterParam?.match,
  });

  const isReportAvailable =
    !reportSummary ||
    reportSummary.billableMinutes + reportSummary.nonBillableMinutes !== 0 ||
    (reportSummary.numberOfUniqueUser ?? 0) > 0;

  const { data: projectList } = useProjectsQuery();

  const { data: nativeActiveUsers = [] } = useNativeActiveOrInvitedUsersQuery();

  const userLookup = useMemo(
    () => keyBy(nativeActiveUsers, 'userId'),
    [nativeActiveUsers],
  );

  return (
    <>
      <Helmet>
        <title>{useTranslation('reports.timesheets.title')}</title>
      </Helmet>
      <Argon
        breadcrumbs={
          <Breadcrumb.Item>
            <Link to="/reports">
              <FormattedMessage id="reports" />
            </Link>
          </Breadcrumb.Item>
        }
        title={
          <HeaderWithIconTitle
            icon={<Timer20 />}
            title={<FormattedMessage id="reports.timesheets.title" />}
          />
        }
        actions={{
          primary: (
            <FilterContainer>
              <TimesheetReportViews
                isFieldsLoading={isFieldsLoading}
                fieldLookup={fields_lookup_by_name}
                fields={fields}
                onFilterChange={onFilterChange}
                currentDefinition={{
                  filter: searchParams.filter,
                }}
                currentViewId={searchParams.viewId}
                onViewSelect={onViewSelect}
              />

              <CustomRangePicker
                range={searchParams.filter.range}
                onDateChange={onRangeChange}
                rangeOptions={RangeOptions}
              />
            </FilterContainer>
          ),
          secondary: isReportAvailable ? (
            <Tooltip
              title={
                <FormattedMessage id="timesheets.reports.tooltips.export" />
              }
              placement="right"
            >
              <Button
                icon={<Export16 />}
                type="text"
                size="small"
                onClick={exportAsCSV}
              />
            </Tooltip>
          ) : null,
        }}
        content={
          !isSummaryLoading && filterParam != null ? (
            <>
              {isReportAvailable ? (
                <TimesheetOverallReportContent
                  summary={reportSummary!}
                  timesheetCategories={timesheetCategories}
                  dateRange={searchParams.filter.range}
                  filterParam={filterParam}
                  projects={projectList ?? []}
                  configuration={configuration}
                  userLookup={userLookup}
                />
              ) : (
                <ReportEmptyState />
              )}
            </>
          ) : (
            <ReportLoadingState />
          )
        }
      />
    </>
  );
};
